import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Layers, Package, Plus, Hammer, Trash2, Edit, Save, 
  Search, Zap, Building2, Grid3X3, Settings, Lock, Shield, Database,
  Info, ChevronRight
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlueprintGrid = () => (
    <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none opacity-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,#0f172a_0%,#020617_100%)]" />
        <div className="absolute inset-0" style={{ 
            backgroundImage: `linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            opacity: 0.3
        }} />
        <div className="absolute inset-0" style={{ 
            backgroundImage: `linear-gradient(#1e293b 0.5px, transparent 0.5px), linear-gradient(90deg, #1e293b 0.5px, transparent 0.5px)`,
            backgroundSize: '8px 8px',
            opacity: 0.1
        }} />
    </div>
);

const DynamicIcon = ({ name, className }) => {
    const Icon = LucideIcons[name || 'Box'] || LucideIcons.Box;
    return <Icon className={className} />;
};

const BlueprintStudioPage = () => {
    const [activeTab, setActiveTab] = useState('verticals');
    const [verticals, setVerticals] = useState([]);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
    const [isVerticalModalOpen, setIsVerticalModalOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [editingVertical, setEditingVertical] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('fluxoco_token');
            const headers = { Authorization: `Bearer ${token}` };
            const [modRes, vertRes] = await Promise.all([
                axios.get(`${API_URL}/api/admin/modules`, { headers }),
                axios.get(`${API_URL}/api/admin/verticals`, { headers })
            ]);
            setModules(modRes.data);
            setVerticals(vertRes.data);
        } catch (error) {
            toast.error("Fallo de conexión en ingeniería");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSaveModule = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('fluxoco_token');
            const { _id, created_at, updated_at, ...cleanModule } = editingModule;
            const payload = { 
                ...cleanModule, 
                id: cleanModule.id.toLowerCase().replace(/ /g, '_'),
                label: cleanModule.label || cleanModule.name
            };
            
            // Si tiene _id o ya existe en la lista, es una actualización técnica
            const isUpdate = !!editingModule._id;
            const method = isUpdate ? 'put' : 'post';
            
            // Usamos el ID técnico (silo path) para la URL como espera el backend
            const url = isUpdate 
                ? `${API_URL}/api/admin/modules/${editingModule.id}`
                : `${API_URL}/api/admin/modules`;

            await axios[method](url, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(isUpdate ? "Silo Sincronizado" : "Estructura de Silo Guardada");
            setIsModuleModalOpen(false);
            fetchData();
        } catch (error) { 
            console.error("Error al guardar silo:", error.response?.data || error.message);
            toast.error("Error al procesar silo"); 
        }
    };

    const handleSaveVertical = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('fluxoco_token');
            const payload = { 
                ...editingVertical, 
                id: editingVertical.id.toLowerCase().replace(/ /g, '_'),
                label: editingVertical.label || editingVertical.name,
                specialty_modules: editingVertical.specialty_modules || editingVertical.modules || []
            };
            await axios.post(`${API_URL}/api/admin/verticals`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Vertical Publicada");
            setIsVerticalModalOpen(false);
            fetchData();
        } catch (error) { toast.error("Error al publicar vertical"); }
    };

    const handleDelete = async (type, id, isCore = false) => {
        if (isCore) {
            toast.error("MODO PROTEGIDO: El Kit 5x5 es inalterable.");
            return;
        }
        if (!window.confirm(`¿Confirmas la remoción del componente?`)) return;
        try {
            const token = localStorage.getItem('fluxoco_token');
            const endpoint = type === 'modules' ? 'modules' : 'verticals';
            await axios.delete(`${API_URL}/api/admin/${endpoint}/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Componente removido");
            fetchData();
        } catch (error) { toast.error("Fallo en eliminación"); }
    };

    const toggleModuleInVertical = (modId) => {
        const currentSelected = editingVertical.specialty_modules || editingVertical.modules || [];
        const newSelected = currentSelected.includes(modId)
            ? currentSelected.filter(id => id !== modId)
            : [...currentSelected, modId];
        setEditingVertical({ ...editingVertical, specialty_modules: newSelected, modules: newSelected });
    };

    const LayerBadge = ({ layer }) => {
        const styles = {
            base: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
            operativo: "bg-blue-500/10 text-blue-400 border-blue-500/20",
            especializado: "bg-purple-500/10 text-purple-400 border-purple-500/20"
        };
        return (
            <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border ${styles[layer] || styles.base}`}>
                {layer || 'Core'}
            </span>
        );
    };

    return (
        <div className="relative min-h-screen pb-20 overflow-hidden space-y-8" data-testid="blueprint-studio-page">
            {/* Header V4.3 - Clonación EXACTA de Data Engine */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                    <div className="flex items-center gap-2 mb-3">
                        <Hammer className="w-5 h-5 text-blue-500" />
                        <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Ingeniería de Giros de Negocio</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
                        Blueprint <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Studio</span>
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium italic text-sm">Arquitectura de Verticales y Silos para negocios especializados</p>
                </motion.div>
                
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="flex items-center gap-2 p-2 bg-red-500/5 rounded-xl border border-red-500/10">
                        <Database className="w-3.5 h-3.5 text-red-500" />
                        <p className="text-[8px] font-bold text-red-400 uppercase tracking-widest">ADN Producción Crítico</p>
                    </div>
                </div>
            </div>

            {/* Control Bar V4.3 - Compacta */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8 p-1 bg-slate-900/40 border border-white/5 rounded-2xl backdrop-blur-md">
                <div className="flex flex-wrap gap-0.5 p-0.5">
                    {[
                        { id: 'verticals', label: 'Verticales de Negocio', icon: Layers, count: verticals.length, activeColor: 'bg-blue-600 shadow-blue-500/30' },
                        { id: 'modules', label: 'Catálogo de Silos', icon: Database, count: modules.length, activeColor: 'bg-emerald-600 shadow-emerald-500/30' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 ${
                                activeTab === tab.id 
                                ? `${tab.activeColor} text-white shadow-xl scale-[1.05]` 
                                : 'text-slate-500 hover:text-white border border-transparent hover:bg-white/5'
                            }`}
                        >
                            <tab.icon className={`w-4 h-4 transition-colors ${activeTab === tab.id ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                            {tab.label}
                            <span className={`ml-1 text-[9px] ${activeTab === tab.id ? 'text-white/60' : 'opacity-30'}`}>({tab.count})</span>
                        </button>
                    ))}
                    <div className="w-px h-6 bg-white/10 mx-2 self-center hidden lg:block" />
                    <div className="relative px-2 w-64 group">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 group-focus-within:text-emerald-400 transition-colors z-10" />
                        <input 
                            type="text" 
                            placeholder="BUSCAR EN EL ADN..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-950/40 border border-white/10 rounded-2xl py-2.5 pl-10 pr-4 text-[10px] font-black tracking-widest text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] focus:outline-none transition-all duration-300" 
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2 pr-1 p-1 lg:p-0">
                    <button 
                        onClick={() => { setEditingModule({ id:'', label:'', icon:'Box', category:'specialty', description:'', ai_context:'' }); setIsModuleModalOpen(true); }} 
                        className="group relative overflow-hidden h-11 px-6 rounded-2xl bg-emerald-600 border border-emerald-500/30 text-white font-black text-[10px] uppercase tracking-[0.2em] hover:bg-emerald-500 transition-all flex items-center gap-2 shadow-xl shadow-emerald-500/20 hover:scale-[1.02]"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/0 via-white/10 to-emerald-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                        <Plus className="w-4 h-4 relative z-10" /> 
                        <span className="relative z-10">Diseñar Silo</span>
                    </button>
                    <button 
                        onClick={() => { setEditingVertical({ id:'', label:'', icon:'Building2', description:'', layer:'base', color:'#06b6d4', specialty_modules:[], quick_win:'' }); setIsVerticalModalOpen(true); }} 
                        className="group relative overflow-hidden h-11 px-6 rounded-2xl bg-blue-600 border border-blue-500/30 text-white font-black text-[10px] uppercase tracking-[0.2em] hover:bg-blue-500 transition-all flex items-center gap-2 shadow-xl shadow-blue-500/20 hover:scale-[1.02]"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                        <Plus className="w-4 h-4 relative z-10" /> 
                        <span className="relative z-10">Nueva Vertical</span>
                    </button>
                </div>
            </div>

            {/* Grid de Contenido V4.3 */}
            {loading ? (
                <div className="h-96 flex flex-col items-center justify-center gap-4 text-center">
                    <div className="animate-spin text-blue-500"><Hammer className="w-12 h-12" /></div>
                    <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500">Analizando ADN de Ingeniería...</span>
                </div>
            ) : (
                <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <AnimatePresence mode="popLayout">
                    {activeTab === 'verticals' ? (
                        verticals.filter(v => (v.label || v.name)?.toLowerCase().includes(searchTerm.toLowerCase())).map((vert, i) => (
                            <motion.div 
                                key={vert.id} 
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: i * 0.03 }}
                                className="group relative"
                            >
                                <div className="relative p-6 rounded-[2.5rem] bg-slate-900/40 border border-white/5 hover:border-blue-500/30 transition-all backdrop-blur-md h-full flex flex-col">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="w-12 h-12 rounded-2xl bg-slate-950/60 border border-white/5 flex items-center justify-center shadow-inner group-hover:border-blue-500/10 transition-all focus-within:ring-2 focus-within:ring-blue-500">
                                            <DynamicIcon name={vert.icon} className="w-6 h-6 text-blue-400 focus:outline-none" />
                                        </div>
                                        <div className="flex gap-1.5 relative z-10">
                                            <button title="Editar Vertical" onClick={(e) => { e.stopPropagation(); setEditingVertical({ ...vert, name: vert.label }); setIsVerticalModalOpen(true); }} className="p-2 bg-white/5 rounded-xl text-slate-500 hover:text-white transition-all"><Edit className="w-3.5 h-3.5" /></button>
                                            <button title="Eliminar Vertical" onClick={(e) => { e.stopPropagation(); handleDelete('verticals', vert.id); }} className="p-2 bg-red-500/5 rounded-xl text-red-500/40 hover:text-red-400 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                    </div>

                                    <div className="space-y-1 mb-4">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-base font-black font-outfit text-white tracking-tight truncate">{vert.label || vert.name}</h3>
                                            <LayerBadge layer={vert.layer} />
                                        </div>
                                        <p className="text-[9px] text-slate-600 font-bold uppercase tracking-wider">{vert.id}</p>
                                    </div>

                                    {vert.quick_win && (
                                        <div className="mb-4 p-3 rounded-xl bg-blue-500/5 border border-blue-500/10 flex gap-2 items-start relative overflow-hidden">
                                            <Zap className="w-3 h-3 text-blue-400 shrink-0 mt-0.5" />
                                            <p className="text-[9px] text-slate-400 font-medium line-clamp-2 leading-relaxed italic pr-1">"{vert.quick_win}"</p>
                                        </div>
                                    )}

                                    <div className="mt-auto flex flex-wrap gap-1.5 pt-4 border-t border-white/5">
                                        {(vert.specialty_modules || vert.modules || []).slice(0, 3).map(mId => {
                                            const modInfo = modules.find(m => m.id === mId);
                                            return (
                                                <div key={mId} className="px-2 py-1 bg-white/5 rounded-lg border border-white/5 flex items-center gap-1.5 shadow-sm" title={modInfo?.label || modInfo?.name}>
                                                    <DynamicIcon name={modInfo?.icon} className="w-2.5 h-2.5 text-blue-500" />
                                                    <span className="text-[8px] font-black text-slate-500 uppercase truncate max-w-[70px]">{modInfo?.label || modInfo?.name || mId}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        modules.filter(m => (m.label || m.name)?.toLowerCase().includes(searchTerm.toLowerCase())).map((mod, i) => {
                            const isCore = mod.category === 'core';
                            const isAddon = mod.category === 'addon';
                            
                            // Paleta de colores según jerarquía ADN
                            const styles = isCore 
                                ? { line: 'bg-amber-500', icon: 'bg-amber-500/10 text-amber-400', label: 'bg-amber-500/20 text-amber-400 border-amber-500/20' }
                                : isAddon 
                                    ? { line: 'bg-purple-500', icon: 'bg-purple-500/10 text-purple-400', label: 'bg-purple-500/20 text-purple-400 border-purple-500/20' }
                                    : { line: 'bg-blue-500', icon: 'bg-blue-500/10 text-blue-400', label: 'bg-blue-500/20 text-blue-400 border-blue-500/20' };

                            return (
                                <motion.div 
                                    key={mod.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.03 }}
                                    className="p-6 rounded-[2.5rem] bg-slate-900/40 border border-white/5 hover:border-blue-500/20 transition-all backdrop-blur-md relative h-full flex flex-col"
                                >
                                    <div className={`absolute top-0 left-0 w-full h-1 opacity-30 ${styles.line}`} />
                                    <div className="flex items-center justify-between mb-5">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-inner ${styles.icon}`}>
                                            {isCore ? <Lock className="w-5 h-5" /> : <DynamicIcon name={mod.icon} className="w-5 h-5" />}
                                        </div>
                                        <div className="flex gap-1.5">
                                            {!isCore && (
                                                <button title="Editar" onClick={() => { setEditingModule({ ...mod, name: mod.label || mod.name }); setIsModuleModalOpen(true); }} className="p-2 bg-white/5 rounded-xl text-slate-500 hover:text-white transition-all"><Edit className="w-3.5 h-3.5" /></button>
                                            )}
                                            {!isCore && (
                                                <button title="Eliminar" onClick={() => handleDelete('modules', mod.id)} className="p-2 bg-red-500/5 rounded-xl text-red-500/40 hover:text-red-400 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                                            )}
                                        </div>
                                    </div>
                                    <h3 className="text-base font-black font-outfit text-white tracking-tight mb-1 truncate">{mod.label || mod.name}</h3>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-[8px] text-slate-600 font-mono font-bold tracking-tighter truncate">{mod.id}</span>
                                        <span className={`px-1.5 py-0.5 rounded-lg text-[7px] font-black uppercase tracking-widest border ${styles.label}`}>
                                            {mod.category?.toUpperCase() || 'SILO'}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed mt-auto font-medium opacity-60 italic">"{mod.description}"</p>
                                </motion.div>
                            );
                        })
                    )}
                    </AnimatePresence>
                </motion.div>
            )}

            {/* Modal: Ensamblaje de Vertical */}
            <Dialog open={isVerticalModalOpen} onOpenChange={setIsVerticalModalOpen}>
                <DialogContent className="sm:max-w-2xl bg-[#0B1120] border-blue-500/20 rounded-[3rem] p-0 overflow-hidden outline-none shadow-2xl">
                    <div className="bg-gradient-to-br from-blue-500/10 to-transparent p-10 max-h-[95vh] overflow-y-auto custom-scrollbar">
                        <DialogHeader>
                            <div className="flex items-center gap-4 mb-2">
                                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shadow-inner">
                                    <Layers className="w-6 h-6 text-blue-400" />
                                </div>
                                <div>
                                    <DialogTitle className="font-outfit text-2xl font-black text-white italic tracking-tighter uppercase">Configurar Vertical</DialogTitle>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Ingeniería de Giro Fluxo Co</p>
                                </div>
                            </div>
                        </DialogHeader>
                        
                        {editingVertical && (
                            <form onSubmit={handleSaveVertical} className="space-y-6 mt-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest pl-1">Identidad de Maquinaria</label>
                                        <input required value={editingVertical.id} onChange={e => setEditingVertical({...editingVertical, id: e.target.value})} placeholder="ej: hospital_v2" disabled={editingVertical._id} className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-xs text-white font-mono focus:border-blue-500/50 outline-none shadow-inner" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest pl-1">Nombre Comercial</label>
                                        <input required value={editingVertical.label || editingVertical.name} onChange={e => setEditingVertical({...editingVertical, name: e.target.value, label: e.target.value})} placeholder="Nombre de Vertical" className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-xs text-white font-bold focus:border-blue-500/50 outline-none shadow-inner" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest pl-1">Capa Operativa</label>
                                        <select value={editingVertical.layer} onChange={e => setEditingVertical({...editingVertical, layer: e.target.value})} className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-xs text-white focus:border-blue-500/50 outline-none appearance-none cursor-pointer">
                                            <option value="base">Vertical Base</option>
                                            <option value="operativo">Vertical Operativa</option>
                                            <option value="especializado">Vertical Especializada</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest pl-1">Selector de Identidad</label>
                                        <div className="grid grid-cols-9 gap-1 bg-slate-950/50 p-2 rounded-xl border border-white/10 h-[100px] overflow-y-auto custom-scrollbar-mini">
                                            {[
                                                'Building2', 'Hospital', 'ShoppingCart', 'Truck', 'Utensils', 'GraduationCap', 'Gavel', 'Briefcase', 'Heart', 'Plane',
                                                'Home', 'Factory', 'Code', 'Smartphone', 'Users', 'Zap', 'Music', 'Gamepad', 'Coffee', 'Dumbbell',
                                                'Camera', 'Globe', 'Shield', 'Star', 'Crown', 'Package', 'Pill', 'Microscope', 'Palette', 'Car',
                                                'Stethoscope', 'Handshake', 'Wallet', 'CreditCard', 'BarChart3', 'PieChart', 'LineChart', 'Target', 'Megaphone', 'ShieldCheck',
                                                'HardHat', 'Wrench', 'Sprout', 'Leaf', 'FlaskConical', 'Boxes', 'Container', 'Warehouse', 'Shirt', 'ChefHat',
                                                'Wine', 'Dna', 'Headphones', 'Trophy', 'Gem', 'Coins', 'Banknote', 'Landmark', 'Flag', 'Construction',
                                                'Drill', 'Ship', 'Radio', 'Wifi', 'Server', 'Layers', 'Brush', 'PenTool', 'Scissors', 'Tent',
                                                'Mountain', 'TreePine', 'Beef', 'Apple', 'Carrot', 'Milk', 'Baby', 'Stroller', 'Bone', 'Dog',
                                                'PawPrint', 'Droplets', 'Wind', 'GlassWater', 'Store', 'Calculator', 'Cpu', 'Cloud', 'Activity', 'Hammer'
                                            ].map(iconName => (
                                                <button 
                                                    key={iconName}
                                                    type="button"
                                                    onClick={() => setEditingVertical({...editingVertical, icon: iconName})}
                                                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all ${editingVertical.icon === iconName ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/40 scale-110' : 'text-slate-600 hover:text-white hover:bg-white/5'}`}
                                                    title={iconName}
                                                >
                                                    <DynamicIcon name={iconName} className="w-4 h-4" />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] flex items-center gap-2">
                                        <Grid3X3 className="w-4 h-4" /> Ensamblaje de ADN (Silos)
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 px-1 max-h-[265px] overflow-y-auto custom-scrollbar-mini pr-2">
                                        {modules.map(mod => {
                                            const isSelected = (editingVertical.specialty_modules || editingVertical.modules || []).includes(mod.id);
                                            return (
                                                <div 
                                                    key={mod.id} 
                                                    onClick={() => toggleModuleInVertical(mod.id)}
                                                    className={`p-2.5 border rounded-xl flex items-center gap-2.5 cursor-pointer transition-all duration-300 ${isSelected ? 'bg-blue-600/25 border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.2)]' : 'bg-white/5 border-white/5 hover:border-white/20'}`}
                                                >
                                                    <DynamicIcon name={mod.icon} className={`w-3.5 h-3.5 ${isSelected ? 'text-blue-300 drop-shadow-[0_0_5px_rgba(59,130,246,0.5)]' : 'text-slate-400'}`} />
                                                    <p className={`text-[9px] font-black truncate uppercase tracking-tighter ${isSelected ? 'text-white' : 'text-slate-200'}`}>{mod.label || mod.name}</p>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest pl-1">Propósito de Vertical</label>
                                        <textarea value={editingVertical.description} onChange={e => setEditingVertical({...editingVertical, description: e.target.value})} placeholder="Impacto del modelo..." className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-xs text-white focus:border-blue-500/50 outline-none h-16 resize-none shadow-inner" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-amber-500 uppercase tracking-widest pl-1 flex items-center gap-2">
                                            <Zap className="w-3" /> Quick-Win AI
                                        </label>
                                        <textarea 
                                            value={editingVertical.quick_win} 
                                            onChange={e => setEditingVertical({...editingVertical, quick_win: e.target.value})} 
                                            placeholder="Describa el logro inmediato de la IA..." 
                                            className="w-full bg-slate-950 border border-amber-500/50 rounded-xl px-4 py-3 text-xs text-amber-400 font-bold focus:border-amber-400 outline-none h-20 resize-none shadow-2xl placeholder:text-amber-600/50" 
                                        />
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <button 
                                        type="submit" 
                                        className="group relative overflow-hidden w-full h-16 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-blue-500/20 active:scale-[0.98]"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                                        <Save className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform" /> 
                                        <span className="relative z-10">Instalar Modelo de Vertical</span>
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal: Diseñar Silo */}
            <Dialog open={isModuleModalOpen} onOpenChange={setIsModuleModalOpen}>
                <DialogContent className="sm:max-w-xl bg-[#0B1120] border-blue-500/20 rounded-[3rem] p-10 outline-none shadow-2xl">
                    <DialogHeader>
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shadow-inner">
                                <Database className="w-6 h-6 text-blue-400" />
                            </div>
                            <DialogTitle className="font-outfit text-3xl font-black text-white italic tracking-tighter uppercase">Diseñar Silo Operativo</DialogTitle>
                        </div>
                    </DialogHeader>
                    {editingModule && (
                        <form onSubmit={handleSaveModule} className="mt-8">
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">Silo Path (ID Inmutable)</label>
                                        <input 
                                            type="text" 
                                            value={editingModule.id} 
                                            onChange={e => setEditingModule({...editingModule, id: e.target.value})}
                                            placeholder="ej: servicios_express"
                                            readOnly={!!editingModule._id}
                                            className={`w-full bg-slate-950/50 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-800 font-mono transition-all ${editingModule._id ? 'opacity-40 cursor-not-allowed bg-slate-900' : 'hover:border-white/20'}`}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">Label Operativo</label>
                                        <input 
                                            type="text" 
                                            value={editingModule.name} 
                                            onChange={e => setEditingModule({...editingModule, name: e.target.value})}
                                            placeholder="ej: Servicios Express"
                                            className="w-full bg-slate-950/50 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-800 hover:border-white/20 transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">Categoría ADN</label>
                                        <div className="relative">
                                            <select 
                                                value={editingModule.category}
                                                onChange={e => setEditingModule({...editingModule, category: e.target.value})}
                                                className="w-full bg-slate-950/50 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none cursor-pointer hover:border-white/20 transition-all"
                                            >
                                                <option value="specialty">Specialty (Giro de Negocio)</option>
                                                <option value="addon">Addon (Funcionalidad Extra)</option>
                                            </select>
                                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                                <ChevronRight className="w-4 h-4 rotate-90" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">Selector de Icono</label>
                                        <div className="grid grid-cols-7 gap-1 p-2 bg-slate-950/50 border border-white/10 rounded-2xl h-[120px] overflow-y-auto custom-scrollbar shadow-inner">
                                            {[
                                                'Zap', 'Package', 'Wrench', 'Truck', 'Utensils', 'GraduationCap', 'Stethoscope', 'Briefcase', 'Heart', 'Home', 'Store', 'Cpu',
                                                'Building2', 'Hospital', 'ShoppingCart', 'Gavel', 'Plane', 'Factory', 'Code', 'Smartphone', 'Users', 'Music', 'Gamepad', 'Coffee',
                                                'Dumbbell', 'Camera', 'Globe', 'Shield', 'Star', 'Crown', 'Pill', 'Microscope', 'Palette', 'Car', 'Handshake', 'Wallet', 
                                                'CreditCard', 'BarChart3', 'PieChart', 'LineChart', 'Target', 'Megaphone', 'ShieldCheck', 'HardHat', 'Sprout', 'Leaf', 
                                                'FlaskConical', 'Boxes', 'Container', 'Warehouse', 'Shirt', 'ChefHat', 'Wine', 'Dna', 'Headphones', 'Trophy', 'Gem', 
                                                'Coins', 'Banknote', 'Landmark', 'Flag', 'Construction', 'Drill', 'Ship', 'Radio', 'Wifi', 'Server', 'Layers', 'Brush', 
                                                'PenTool', 'Scissors', 'Tent', 'Mountain', 'TreePine', 'Beef', 'Apple', 'Carrot', 'Milk', 'Baby', 'Stroller', 'Bone', 
                                                'Dog', 'PawPrint', 'Droplets', 'Wind', 'GlassWater', 'Calculator', 'Cloud', 'Activity', 'Hammer'
                                            ].map(iconName => (
                                                <button
                                                    key={iconName}
                                                    type="button"
                                                    onClick={() => setEditingModule({...editingModule, icon: iconName})}
                                                    className={`flex items-center justify-center p-1 rounded-lg transition-all ${editingModule.icon === iconName ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-600 hover:bg-white/5 hover:text-slate-300'}`}
                                                >
                                                    <DynamicIcon name={iconName} className="w-4 h-4" />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">Propósito Silo</label>
                                    <textarea 
                                        value={editingModule.description} 
                                        onChange={e => setEditingModule({...editingModule, description: e.target.value})}
                                        placeholder="Defina la función atómica de este silo..."
                                        rows={3}
                                        className="w-full bg-slate-950/50 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-800 resize-none hover:border-white/20 transition-all shadow-inner"
                                    />
                                </div>
                            </div>

                            <button type="submit" className="w-full h-16 mt-10 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-[0.4em] rounded-[2.5rem] transition-all shadow-xl shadow-blue-500/10 active:scale-[0.98] flex items-center justify-center gap-3">
                                <Database className="w-4 h-4" /> Sincronizar Átomo de Datos
                            </button>
                        </form>
                    )}
                </DialogContent>
        </Dialog>


            <BlueprintGrid />
        </div>
    );
};

export default BlueprintStudioPage;
